import React, { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { formatINR } from "@/components/ChefCard";
import ImageUpload from "@/components/ImageUpload";
import GalleryAdder from "@/components/GalleryAdder";
import { ShieldCheck, ChefHat } from "lucide-react";

const CITIES = ["Mumbai", "Bengaluru", "Indore", "Chennai", "Goa", "Delhi"];
const CUISINE_OPTIONS = ["Modern Indian", "Coastal", "South Indian", "Hyderabadi", "Italian", "Japanese", "French", "Continental", "North Indian", "Levantine", "Chinese"];
const DIET_OPTIONS = ["Veg", "Non-Veg", "Jain", "Vegan", "Keto"];

export default function ChefPortal() {
  const { user, setAuthOpen, refresh } = useAuth();
  const [profile, setProfile] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gallery, setGallery] = useState([]);
  const [savingGallery, setSavingGallery] = useState(false);

  const saveGallery = async () => {
    setSavingGallery(true);
    try {
      await api.patch("/chefs/me/gallery", { gallery });
      toast.success("Dish photos updated — they're live on your profile");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save photos"); }
    finally { setSavingGallery(false); }
  };

  const [form, setForm] = useState({
    name: "", photo_url: "", bio: "",
    cuisines: [], signature_dishes: "",
    experience_yrs: 0, languages: "",
    diet_tags: [], cities: [],
    salary_type: "indian", monthly_salary: 55000, gallery: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const [p, b] = await Promise.all([
        api.get("/chefs/me/profile"),
        api.get("/bookings"),
      ]);
      setProfile(p.data.chef);
      if (p.data.chef) {
        setForm({
          name: p.data.chef.name || user?.name || "",
          photo_url: p.data.chef.photo_url || "",
          bio: p.data.chef.bio || "",
          cuisines: p.data.chef.cuisines || [],
          signature_dishes: (p.data.chef.signature_dishes || []).join(", "),
          experience_yrs: p.data.chef.experience_yrs || 0,
          languages: (p.data.chef.languages || []).join(", "),
          diet_tags: p.data.chef.diet_tags || [],
          cities: p.data.chef.cities || [],
          salary_type: p.data.chef.salary_type || "indian",
          monthly_salary: p.data.chef.monthly_salary || 55000,
          gallery: (p.data.chef.gallery || []).join(", "),
        });
        setGallery(p.data.chef.gallery || []);
      } else {
        setForm((f) => ({ ...f, name: user?.name || "" }));
      }
      setBookings(b.data.bookings || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  useEffect(() => { if (user) load(); }, [user]);

  const save = async () => {
    try {
      const payload = {
        ...form,
        signature_dishes: form.signature_dishes.split(",").map(s => s.trim()).filter(Boolean),
        languages: form.languages.split(",").map(s => s.trim()).filter(Boolean),
        gallery,
        experience_yrs: parseInt(form.experience_yrs) || 0,
        monthly_salary: parseInt(form.monthly_salary) || 0,
      };
      await api.post("/chefs", payload);
      toast.success(profile?.status === "approved" ? "Profile saved." : "Profile saved. Awaiting admin approval.");
      await refresh();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const toggleArrayItem = (key, item) => {
    setForm((f) => ({
      ...f,
      [key]: f[key].includes(item) ? f[key].filter(x => x !== item) : [...f[key], item],
    }));
  };

  const setStatus = async (id, s) => {
    try {
      await api.patch(`/bookings/${id}/status`, { status: s });
      toast.success(`Marked ${s}`);
      load();
    } catch (e) { toast.error("Failed"); }
  };

  if (!user) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <h1 className="font-serif-display text-4xl">Chef Portal</h1>
        <p className="text-neutral-400 mt-3">Sign in to manage your profile and bookings.</p>
        <Button onClick={() => setAuthOpen(true)} className="mt-6 rounded-none bg-[#C04000] hover:bg-[#A03500] h-11 small-caps text-[11px]">
          Sign in
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-16">
      <div className="flex items-center gap-3 small-caps text-neutral-500 mb-3">
        <ChefHat size={14} /> Chef · Manager Portal
      </div>
      <h1 className="font-serif-display text-5xl">Your kitchen. Your rhythm.</h1>
      <p className="text-neutral-400 mt-3">Manage your profile, availability and assignments.</p>

      {profile && (
        <div className="mt-6 flex items-center gap-3">
          <span className={`chip capitalize ${profile.status === "approved" ? "text-green-400 border-green-400/40" : profile.status === "pending" ? "text-[#F5B041] border-[#F5B041]/40" : "text-red-400 border-red-400/40"}`}>
            <ShieldCheck size={10} /> {profile.status}
          </span>
          {profile.status === "pending" && <span className="text-xs text-neutral-500">Awaiting admin approval</span>}
        </div>
      )}

      <div className="dotted-sep my-10" />

      <Tabs defaultValue="profile" className="w-full">
        <TabsList className="grid grid-cols-3 md:w-[520px] rounded-none bg-transparent border border-white/10 p-0 h-11">
          <TabsTrigger value="profile" data-testid="tab-profile" className="rounded-none small-caps text-[10px] data-[state=active]:bg-white/5 h-full">Profile</TabsTrigger>
          <TabsTrigger value="gallery" data-testid="tab-gallery" className="rounded-none small-caps text-[10px] data-[state=active]:bg-white/5 h-full">Dish photos</TabsTrigger>
          <TabsTrigger value="assignments" data-testid="tab-assignments" className="rounded-none small-caps text-[10px] data-[state=active]:bg-white/5 h-full">Assignments</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="mt-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div>
                <Label className="small-caps text-[10px] text-neutral-500">Full name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="chef-name" className="mt-2 rounded-none bg-transparent border-white/10 h-11" />
              </div>
              <div>
                <Label className="small-caps text-[10px] text-neutral-500 block mb-2">Profile photo</Label>
                <ImageUpload value={form.photo_url} onChange={(url) => setForm({ ...form, photo_url: url })} testId="chef-photo" />
              </div>
              <div>
                <Label className="small-caps text-[10px] text-neutral-500">Bio</Label>
                <Textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })}
                  data-testid="chef-bio" placeholder="Where you've cooked, what you love…"
                  className="mt-2 rounded-none bg-transparent border-white/10 min-h-32" />
              </div>
              <div>
                <Label className="small-caps text-[10px] text-neutral-500">Signature dishes (comma-separated)</Label>
                <Input value={form.signature_dishes} onChange={(e) => setForm({ ...form, signature_dishes: e.target.value })}
                  data-testid="chef-dishes" className="mt-2 rounded-none bg-transparent border-white/10 h-11" />
              </div>
              <div>
                <Label className="small-caps text-[10px] text-neutral-500">Languages (comma-separated)</Label>
                <Input value={form.languages} onChange={(e) => setForm({ ...form, languages: e.target.value })}
                  data-testid="chef-languages" className="mt-2 rounded-none bg-transparent border-white/10 h-11" />
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <Label className="small-caps text-[10px] text-neutral-500">Experience (years)</Label>
                <Input type="number" value={form.experience_yrs} onChange={(e) => setForm({ ...form, experience_yrs: e.target.value })}
                  data-testid="chef-experience" className="mt-2 rounded-none bg-transparent border-white/10 h-11" />
              </div>
              <div>
                <Label className="small-caps text-[10px] text-neutral-500">Roster</Label>
                <Select value={form.salary_type} onValueChange={(v) => setForm({ ...form, salary_type: v })}>
                  <SelectTrigger data-testid="chef-roster" className="mt-2 rounded-none bg-transparent border-white/10 h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none bg-[#141414] border-white/10">
                    <SelectItem value="indian" className="rounded-none">India (₹50k–₹1.25L)</SelectItem>
                    <SelectItem value="international" className="rounded-none">International (₹90k–₹3.55L)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="small-caps text-[10px] text-neutral-500">Monthly salary (₹)</Label>
                <Input type="number" value={form.monthly_salary} onChange={(e) => setForm({ ...form, monthly_salary: e.target.value })}
                  data-testid="chef-salary" className="mt-2 rounded-none bg-transparent border-white/10 h-11" />
              </div>

              <div>
                <Label className="small-caps text-[10px] text-neutral-500 block mb-2">Cuisines</Label>
                <div className="flex flex-wrap gap-2">
                  {CUISINE_OPTIONS.map((c) => (
                    <button key={c} type="button" onClick={() => toggleArrayItem("cuisines", c)}
                      className={`chip cursor-pointer ${form.cuisines.includes(c) ? "border-[#C04000] text-[#F5B041]" : ""}`}>
                      {c}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="small-caps text-[10px] text-neutral-500 block mb-2">Cities</Label>
                <div className="flex flex-wrap gap-2">
                  {CITIES.map((c) => (
                    <button key={c} type="button" onClick={() => toggleArrayItem("cities", c)}
                      className={`chip cursor-pointer ${form.cities.includes(c) ? "border-[#C04000] text-[#F5B041]" : ""}`}>
                      {c}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="small-caps text-[10px] text-neutral-500 block mb-2">Diet</Label>
                <div className="flex flex-wrap gap-2">
                  {DIET_OPTIONS.map((c) => (
                    <button key={c} type="button" onClick={() => toggleArrayItem("diet_tags", c)}
                      className={`chip cursor-pointer ${form.diet_tags.includes(c) ? "border-[#C04000] text-[#F5B041]" : ""}`}>
                      {c}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8">
            <Button onClick={save} data-testid="save-chef-profile"
              className="rounded-none bg-[#C04000] hover:bg-[#A03500] h-11 small-caps text-[11px] px-8">
              Save profile
            </Button>
            <p className="text-[10px] text-neutral-500 mt-3">Profile is verified by our admin team before going live on the roster.</p>
          </div>
        </TabsContent>

        <TabsContent value="gallery" className="mt-8" data-testid="chef-gallery-tab">
          <div className="max-w-3xl">
            <h3 className="font-serif-display text-3xl">Your dish photos</h3>
            <p className="text-neutral-400 text-sm mt-2">
              Add photos of dishes you've cooked — they appear on your profile straight away. Up to 24 photos.
            </p>
            <div className="dotted-sep my-6" />
            {gallery.length === 0 ? (
              <div className="border border-white/10 py-10 text-center text-neutral-500 text-sm">
                No dish photos yet. Add your first one below.
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {gallery.map((g, i) => (
                  <div key={`${g}-${i}`} className="relative group">
                    <img src={g} alt={`Dish ${i + 1}`} className="w-full aspect-square object-cover border border-white/10" />
                    <button onClick={() => setGallery(gallery.filter((_, j) => j !== i))}
                      data-testid={`chef-gallery-remove-${i}`}
                      className="absolute top-1 right-1 bg-black/80 border border-white/20 text-white w-6 h-6 text-xs leading-none md:opacity-0 md:group-hover:opacity-100 transition">
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-6">
              <Label className="small-caps text-[10px] text-neutral-500 block mb-2">Add a dish photo</Label>
              <GalleryAdder testId="chef-gallery-upload"
                onAdd={(url) => setGallery((g) => (g.length >= 24 ? g : [...g, url]))} />            </div>

            <Button onClick={saveGallery} disabled={savingGallery} data-testid="save-chef-gallery"
              className="mt-6 rounded-none bg-[#C04000] hover:bg-[#A03500] h-11 small-caps text-[11px] px-8">
              {savingGallery ? "Saving…" : "Save dish photos"}
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="assignments" className="mt-8">
          {bookings.length === 0 ? (
            <div className="text-neutral-500">No bookings assigned yet.</div>
          ) : (
            <div className="space-y-4" data-testid="chef-assignments">
              {bookings.map((b) => (
                <div key={b.id} className="card-lux p-6">
                  <div className="grid md:grid-cols-4 gap-4 items-center">
                    <div className="md:col-span-2">
                      <div className="small-caps text-[10px] text-neutral-500">{b.service_type?.replace("_"," ")} · {b.city}</div>
                      <div className="font-serif-menu text-xl mt-1">{b.customer_name}</div>
                      <div className="text-sm text-neutral-400 mt-1">
                        {b.start_date || b.event_date} · {b.duration_months ? `${b.duration_months} mo` : `${b.guests || "—"} guests`}
                      </div>
                    </div>
                    <div>
                      <span className="chip capitalize">{b.status.replace("_", " ")}</span>
                    </div>
                    <div className="flex flex-wrap gap-2 justify-end">
                      {b.status === "assigned" && (
                        <Button size="sm" onClick={() => setStatus(b.id, "confirmed")} data-testid={`confirm-${b.id}`}
                          className="rounded-none bg-[#C04000] hover:bg-[#A03500] h-9 small-caps text-[10px]">Accept</Button>
                      )}
                      {["confirmed","paid"].includes(b.status) && (
                        <Button size="sm" onClick={() => setStatus(b.id, "in_progress")}
                          className="rounded-none bg-[#F5B041] text-black hover:bg-[#e0a038] h-9 small-caps text-[10px]">Start</Button>
                      )}
                      {b.status === "in_progress" && (
                        <Button size="sm" onClick={() => setStatus(b.id, "completed")}
                          className="rounded-none bg-neutral-800 hover:bg-neutral-700 h-9 small-caps text-[10px]">Complete</Button>
                      )}
                    </div>
                  </div>
                  {b.notes && <p className="text-sm text-neutral-500 mt-3 border-t border-white/5 pt-3">{b.notes}</p>}
                  {(b.dietary_preferences?.length > 0 || b.allergies?.length > 0 || b.kitchen_equipment?.length > 0 || b.chef_notes) && (
                    <div className="mt-3 border-t border-white/5 pt-3 space-y-2 text-xs">
                      {b.dietary_preferences?.length > 0 && (
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="small-caps text-[9px] text-neutral-500">Diet</span>
                          {b.dietary_preferences.map(d => <span key={d} className="chip text-[#F5B041] text-[9px]">{d}</span>)}
                        </div>
                      )}
                      {b.allergies?.length > 0 && (
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="small-caps text-[9px] text-neutral-500">⚠ Allergies</span>
                          {b.allergies.map(a => <span key={a} className="chip text-[#C04000] border-[#C04000]/40 text-[9px]">{a}</span>)}
                        </div>
                      )}
                      {b.chef_notes && <p className="text-neutral-300"><span className="small-caps text-[9px] text-neutral-500">Client note ·</span> {b.chef_notes}</p>}
                      {b.kitchen_equipment?.length > 0 && (
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="small-caps text-[9px] text-neutral-500">Kitchen</span>
                          <span className="text-neutral-400">{b.kitchen_equipment.slice(0, 6).join(" · ")}{b.kitchen_equipment.length > 6 ? "…" : ""}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
